import { useState } from "react";
import { ChevronDown, Wrench } from "lucide-react";
import { cn, Spinner } from "@ui";
import { getWidget } from "@agentic_system/widgets";

// Shared shape for a single tool invocation. Used by:
//   - the live chat (agentic_conversation) — adapted from Message#tool_call_result
//   - the schema builder preview (agentic_system) — written by SchemaBuilderJob
//
// Keep the fields in sync with both backends. The nested `result` payload
// intentionally uses `component_key` so getWidget() can pick a renderer.
export interface ToolCallEntryData {
  tool_name?: string;
  display_name?: string | null;
  /** Raw id from RubyLLM / the chat message; used as a stable react key when present. */
  tool_call_id?: string | null;
  arguments?: Record<string, unknown>;
  result?: {
    component_key?: string;
    count?: number;
    [key: string]: unknown;
  } | string | null;
  /** "calling" / "progress" mean in-flight, "error" renders the error style. */
  status?: "calling" | "progress" | "success" | "error" | "pending" | null;
  /** Optional free-form fallback label when tool_name/display_name are absent. */
  content?: string | null;
}

interface Props {
  entry: ToolCallEntryData;
  isLast?: boolean;
}

export function ToolCallEntry({ entry, isLast = false }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = entry.status;
  const isProgress = status === "progress" || status === "calling" || status === "pending";
  const isError = status === "error";

  const resultObj =
    entry.result && typeof entry.result === "object" ? entry.result : null;
  const hasResults = !!(resultObj && Object.keys(resultObj).length > 0);
  const componentKey = resultObj?.component_key || null;
  const Widget = componentKey ? getWidget(componentKey) : null;

  const label =
    entry.display_name ||
    entry.tool_name ||
    entry.content ||
    "Outil";
  const resultCount = resultObj?.count;

  const renderToolResult = () => {
    if (!hasResults) return null;

    if (Widget && resultObj) {
      const widgetData = (resultObj as Record<string, unknown>).result || resultObj;
      const countForWidget =
        (widgetData as { count?: number } | undefined)?.count ?? resultObj.count;
      return <Widget data={widgetData} count={countForWidget} />;
    }

    return (
      <div className="rounded border border-slate-200 bg-white p-2 min-w-0 max-w-full">
        <pre className="overflow-x-auto text-xs whitespace-pre-wrap break-all max-w-full">
          {JSON.stringify(entry.result, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div className="relative flex items-start gap-3 w-full min-w-0">
      {/* Timeline connector */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full",
            isProgress
              ? "bg-default-100"
              : isError
                ? "bg-red-100"
                : "bg-slate-100",
          )}
        >
          {isProgress ? (
            <Spinner size="xs" />
          ) : (
            <Wrench
              className={cn(
                "h-3 w-3",
                isError ? "text-red-500" : "text-slate-500",
              )}
            />
          )}
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 min-h-[12px]" />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-3 min-w-0">
        <button
          onClick={() => hasResults && setIsExpanded(!isExpanded)}
          disabled={!hasResults}
          className={cn(
            "flex items-center gap-2 text-left",
            hasResults && "hover:text-slate-900 cursor-pointer",
          )}
        >
          <span className="text-sm font-medium text-slate-600">{label}</span>
          {isProgress && (
            <span className="text-xs text-slate-400">en cours...</span>
          )}
          {isError && <span className="text-xs text-red-500">erreur</span>}
          {!isProgress && !isError && resultCount !== undefined && (
            <span className="text-xs text-slate-400">
              {resultCount} résultat{resultCount !== 1 ? "s" : ""}
            </span>
          )}
          {hasResults && (
            <ChevronDown
              className={cn(
                "h-4 w-4 text-slate-400 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          )}
        </button>

        {isExpanded && hasResults && (
          <div className="mt-2 min-w-0 overflow-hidden">
            {renderToolResult()}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Adapt an AgenticConversation Message (template_type: "tool") into the
 * generic ToolCallEntryData shape this component expects. Kept here so both
 * frontends stay in sync on the contract.
 *
 * In the chat, `tool_call_result` is a wrapper carrying tool_name /
 * display_name / arguments alongside the tool's actual output under
 * `.result`. For widget lookup and count display we unwrap that inner
 * payload, falling back to the wrapper itself if `.result` is absent
 * (older records or fixtures).
 */
export function toolCallEntryFromMessage(message: {
  status?: string | null;
  content?: string | null;
  tool_call_result?: Record<string, unknown> | null | undefined;
}): ToolCallEntryData {
  const tcr = message.tool_call_result || {};
  const innerResult =
    tcr.result && typeof tcr.result === "object"
      ? (tcr.result as Record<string, unknown>)
      : (tcr as Record<string, unknown>);

  return {
    tool_name: typeof tcr.tool_name === "string" ? tcr.tool_name : undefined,
    display_name:
      typeof tcr.display_name === "string" ? tcr.display_name : undefined,
    arguments:
      tcr.arguments && typeof tcr.arguments === "object"
        ? (tcr.arguments as Record<string, unknown>)
        : undefined,
    result: innerResult as ToolCallEntryData["result"],
    status: (message.status ?? undefined) as ToolCallEntryData["status"],
    content: message.content ?? undefined,
  };
}
